/**
 * PHONE THUNKS — Async thunks per add/update/remove polimorfico.
 *
 * Dopo ogni mutazione, dispatch del fetch dell'entita' owner corretta
 * per rinfrescare il Redux state.
 *
 * Instradamento API per-owner (entrambi nested, niente flat):
 *   - className === 'Person'   -> personPhoneApi
 *   - className === 'Business' -> businessPhoneApi
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { extractError } from '../../store/thunkHelpers';
import { personPhoneApi } from '../person/personPhoneApi';
import { businessPhoneApi } from '../business/businessPhoneApi';
import type {
  Phone,
  CreatePhonePayload,
  UpdatePhonePayload,
  RemovePhonePayload,
  PhoneOwnerClassName,
} from './phoneTypes';
import { fetchPerson } from '../person/personThunks';
import { fetchBusiness } from '../business/businessThunks';
import type { AppDispatch } from '../../store/store';

function dispatchOwnerRefresh(dispatch: AppDispatch, className: PhoneOwnerClassName, classPK: number): void {
  if (className === 'Person') dispatch(fetchPerson(classPK));
  else if (className === 'Business') dispatch(fetchBusiness(classPK));
}

export const addPhone = createAsyncThunk<Phone, CreatePhonePayload>(
  'phones/add',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const body = { number: payload.number, primary: payload.primary };
      const created = payload.className === 'Person'
        ? await personPhoneApi.create(payload.classPK, body)
        : await businessPhoneApi.create(payload.classPK, body);
      dispatchOwnerRefresh(dispatch as AppDispatch, payload.className, payload.classPK);
      return created;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const updatePhone = createAsyncThunk<Phone, UpdatePhonePayload>(
  'phones/update',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const body = { number: payload.number, primary: payload.primary };
      const updated = payload.className === 'Person'
        ? await personPhoneApi.update(payload.classPK, payload.phoneId, body)
        : await businessPhoneApi.update(payload.classPK, payload.phoneId, body);
      dispatchOwnerRefresh(dispatch as AppDispatch, payload.className, payload.classPK);
      return updated;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const removePhone = createAsyncThunk<number, RemovePhonePayload>(
  'phones/remove',
  async ({ phoneId, className, classPK }, { dispatch, rejectWithValue }) => {
    try {
      if (className === 'Person') {
        await personPhoneApi.remove(classPK, phoneId);
      } else {
        await businessPhoneApi.remove(classPK, phoneId);
      }
      dispatchOwnerRefresh(dispatch as AppDispatch, className, classPK);
      return phoneId;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);
